import { useEffect, useState } from "react";
import { useWidget } from "../../../providers/widget";
import { GalleryWidgetConfig } from "../config";
import { resolveWidgetSpacingStyle } from "@homerunner-next/widget-core/spacing"

interface GallerySkeletonProps {
  className?: string;
}

export default function GallerySkeleton({ className = "" }: GallerySkeletonProps) {
  const { options, resolvedTheme } = useWidget<GalleryWidgetConfig>();

  const [isMobile, setIsMobile] = useState(true);

  useEffect(() => {
    const handleBreakpointChange = () => {
      setIsMobile(window.innerWidth < 768);
    };
    handleBreakpointChange();
    window.addEventListener("resize", handleBreakpointChange);
    return () => {
      window.removeEventListener("resize", handleBreakpointChange);
    };
  }, []);

  // Determine which layout to show based on style and screen size
  const shouldShowCarousel = options.style === 'simple' || (options.style === 'masonry' && isMobile);

  // Get responsive images to show based on screen size (matching Carousel logic)
  const getResponsiveImagesToShow = () => {
    if (typeof window === 'undefined') return options.carousel.imagesToShow ?? 1;
    
    if (window.innerWidth >= 1200) return options.carousel.imagesToShow1200 ?? 4;
    if (window.innerWidth >= 780) return options.carousel.imagesToShow780 ?? 3;
    return 1;
  };

  const [responsiveImagesToShow, setResponsiveImagesToShow] = useState(getResponsiveImagesToShow);

  useEffect(() => {
    const handleResize = () => {
      setResponsiveImagesToShow(getResponsiveImagesToShow());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [options.carousel.imagesToShow, options.carousel.imagesToShow780, options.carousel.imagesToShow1200]);

  // Mobile carousel skeleton
  const MobileCarouselSkeleton = () => (
    <div className="homerunner-gallery-masonry-mobile">
      <div className="homerunner-gallery-carousel w-full">
        <div className="homerunner-gallery-carousel-wrapper relative">
          {/* Navigation buttons skeleton */}
          <div className="absolute left-4 top-1/2 -translate-y-1/2 z-10 rounded-full p-1 w-10 h-10 homerunner-gallery-skeleton-pulse" />
          <div className="absolute right-4 top-1/2 -translate-y-1/2 z-10 rounded-full p-1 w-10 h-10 homerunner-gallery-skeleton-pulse" />
          
          {/* Carousel container skeleton */}
          <div className="homerunner-gallery-carousel-container overflow-hidden rounded-lg">
            <div className="homerunner-gallery-carousel-track flex">
              {Array.from({ length: responsiveImagesToShow }).map((_, index) => (
                <div 
                  key={index}
                  className="flex-[0_0_auto] min-w-0"
                  style={{ 
                    flexBasis: `${100 / responsiveImagesToShow}%`,
                    paddingRight: (index < responsiveImagesToShow - 1 && responsiveImagesToShow !== 1) ? '0.5rem' : '0'
                  }}
                >
                  <div className="gallery-image-container aspect-[4/3] homerunner-gallery-skeleton">
                    {/* Show all photos button skeleton on first image */}
                    {index === 0 && options.showAllPhotoButton && (
                      <div className="absolute bottom-4 left-4 bg-card text-card-foreground px-3 py-2 rounded-lg shadow-lg flex items-center gap-2 text-sm font-medium z-10 homerunner-gallery-skeleton-pulse">
                        <div className="w-4 h-4 bg-muted rounded" />
                        <div className="w-24 h-4 bg-muted rounded" />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Desktop 2x5 grid skeleton
  const DesktopTwoByFiveGridSkeleton = () => (
    <div className="homerunner-gallery-masonry-desktop">
      <div className="homerunner-gallery-two-by-five-grid">
        <div className="homerunner-gallery-two-by-five-grid-container w-full grid grid-cols-2 gap-2">
          {/* First column - Single large image skeleton */}
          <div className="homerunner-gallery-two-by-five-main-image gallery-image-container aspect-[16/9] homerunner-gallery-skeleton" />
          
          {/* Second column - 2x2 grid of remaining images skeleton */}
          <div className="homerunner-gallery-two-by-five-thumbnails grid grid-cols-2 grid-rows-2 gap-2">
            {Array.from({ length: 4 }).map((_, index) => (
              <div 
                key={index} 
                className="homerunner-gallery-two-by-five-thumbnail gallery-image-container aspect-[16/9] homerunner-gallery-skeleton relative"
              >
                {/* Show all photos button skeleton on bottom-right image (index 3) */}
                {index === 3 && options.showAllPhotoButton && (
                  <div className="absolute bottom-5 right-5 bg-card text-card-foreground px-3 py-2 rounded-lg shadow-lg flex items-center gap-2 text-sm font-medium z-10 homerunner-gallery-skeleton-pulse">
                    <div className="w-4 h-4 bg-muted rounded" />
                    <div className="w-24 h-4 bg-muted rounded" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div 
      className={`homerunner-gallery-container homerunner-gallery-app w-full ${resolvedTheme === 'dark' ? 'dark' : ''} ${className}`}
      style={resolveWidgetSpacingStyle(options)}
    >
      {options.style === 'simple' ? (
        <div className="homerunner-gallery-simple-style-wrapper">
          <MobileCarouselSkeleton />
        </div>
      ) : (
        <div className="homerunner-gallery-masonry-style-wrapper">
          {shouldShowCarousel ? <MobileCarouselSkeleton /> : <DesktopTwoByFiveGridSkeleton />}
        </div>
      )}
    </div>
  );
}
